import React, { createContext, useContext, useState, useCallback } from 'react';
import { authRepository } from '../infrastructure/authApiRepository';

const STORAGE_KEY = 'rb_auth';

function loadFromStorage() {
    try {
        const data = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (!data) return null;
        try {
            const payload = JSON.parse(atob(data.accessToken.split('.')[1]));
            if (payload.exp && payload.exp * 1000 < Date.now()) {
                localStorage.removeItem(STORAGE_KEY);
                return null;
            }
        } catch {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return data;
    } catch {
        return null;
    }
}

function toMember(auth) {
    if (!auth) return null;
    return {
        memberId: auth.memberId,
        email: auth.email,
        role: auth.role,
        companyId: auth.companyId ?? null,
        companyStatus: auth.companyStatus ?? null,
    };
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [auth, setAuth] = useState(loadFromStorage);

    const saveAuth = useCallback((data) => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        setAuth(data);
    }, []);

    const clearAuth = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        setAuth(null);
    }, []);

    const login = useCallback(async (email, password) => {
        try {
            const data = await authRepository.login({ email, password });
            saveAuth(data);
            return data;
        } catch (err) {
            if (err.status === 401 || err.status === 403) clearAuth();
            throw err;
        }
    }, [saveAuth, clearAuth]);

    const logout = useCallback(async () => {
        if (auth?.refreshToken) {
            await authRepository.logout(auth.refreshToken).catch(() => {});
        }
        clearAuth();
    }, [auth, clearAuth]);

    const updateCompanyInfo = useCallback((updates) => {
        if (!auth) return;
        const updated = { ...auth, ...updates };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        setAuth(updated);
    }, [auth]);

    const refreshMember = useCallback(async () => {
        if (!auth?.accessToken) return;
        const info = await authRepository.getMe(auth.accessToken);
        const updated = { ...auth, ...info };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        setAuth(updated);
    }, [auth]);

    const value = {
        member: toMember(auth),
        accessToken: auth?.accessToken ?? null,
        isLoggedIn: !!auth,
        login,
        logout,
        saveAuth,
        clearAuth,
        updateCompanyInfo,
        refreshMember,
        // 하위 호환
        updateCompanyStatus: (status) => updateCompanyInfo({ companyStatus: status }),
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
    return ctx;
}
